import { environmentConfig, isTestNodeEnvironment } from "@src/appV2/environment";
import { APP_V2_APP_EVENTS, getAppInfo, isCapacitorPlatform } from "@src/appV2/lib";
import { logError, logEvent } from "@src/appV2/lib/analytics";
import { clearPreferences } from "@src/appV2/NativeAdapters/preferences.adapter";
import { type Worker } from "@src/appV2/Worker/api/types";
import { createSegmentInstance, identifyUser } from "@src/lib/analytics";
import { ZendeskSDK } from "capacitor-zendesk-sdk";
import { enqueueSnackbar } from "notistack";
import { type Dispatch } from "redux";

import { fetchEnvVariables, updateAgentData } from "./api";
import { ActionType, LocalStorage } from "./session.model";
import { getZendeskJWTMessagingSDK } from "../../api/zendesk";
import { clearDatadogRumUser, identifyDatadogRumUser } from "../../utils/datadog";

async function initAnalytics(): Promise<void> {
  void createSegmentInstance({
    key: environmentConfig.REACT_APP_SEGMENT_KEY,
    androidKey: environmentConfig.REACT_APP_SEGMENT_ANDROID_KEY,
    iosKey: environmentConfig.REACT_APP_SEGMENT_IOS_KEY,
    webKey: environmentConfig.REACT_APP_SEGMENT_WEB_KEY,
  });
}

const IS_TEST_ENV = isTestNodeEnvironment();
if (!IS_TEST_ENV) {
  void initAnalytics();
}

export const loadCustomFlags = (dispatch: Dispatch): void => {
  const isSignupFlag = localStorage.getItem("isSignup");

  if (isSignupFlag === "true") {
    dispatch({
      type: ActionType.SET_IS_SIGNUP,
    });
  }
};

export const setEnvVariables = async (dispatch: Dispatch): Promise<void> => {
  const data = await fetchEnvVariables();
  dispatch({
    type: ActionType.SET_ENV_VARIABLES,
    data,
  });
};

export const onLoggedOut = (dispatch: Dispatch): void => {
  try {
    logEvent(APP_V2_APP_EVENTS.ON_LOGGEDOUT_FUNCTION_STARTED);
    dispatch({
      type: ActionType.LOGGED_OUT,
    });

    localStorage.removeItem(LocalStorage.RECOGNIZED_HOLIDAYS);
    localStorage.removeItem("claimCheck");
    localStorage.removeItem("isSignup");
    void clearPreferences();
    clearDatadogRumUser();
    logEvent(APP_V2_APP_EVENTS.ON_LOGGEDOUT_FUNCTION_ENDED);
  } catch (error) {
    logError(APP_V2_APP_EVENTS.ON_LOGGEDOUT_FUNCTION_ERROR, { error });
  }
};

export const identifyAgent = async (worker: Worker): Promise<boolean | undefined> => {
  if (!worker) {
    return;
  }

  if (worker.isFirstSession) {
    await updateAgentData({
      agentId: worker.userId,
      isFirstSession: false,
    });
  }

  const { otaBuildId, version } = await getAppInfo();

  identifyDatadogRumUser(worker);

  identifyUser({
    worker,
    otaBuildId,
    version,
  });

  return true;
};

export const showAgentProfileFailedToLoadError = (dispatch: Dispatch) => {
  enqueueSnackbar({
    variant: "error",
    message: "Error occurred while fetching worker details",
  });
  dispatch({
    type: ActionType.SHOW_FETCH_WORKER_FAILURE_ALERT,
    data: {
      isFetchAgentProfileFailureAlertEnabled: true,
    },
  });
};

export const loginZendesk = async (): Promise<void> => {
  if (!isCapacitorPlatform()) {
    return;
  }

  try {
    const token = await getZendeskJWTMessagingSDK();
    await ZendeskSDK.loginUser({ token });
  } catch (error) {
    logError(APP_V2_APP_EVENTS.ZENDESK_LOGIN_ERROR, { error });
  }
};
